/**
 * VoiceMicButton — drop-in mic icon button that fills a controlled
 * text input via the existing `useVoiceSearch` hook (Web Speech with
 * Whisper fallback).
 *
 * Usage:
 *   <input value={q} onChange={...} className="…"/>
 *   <VoiceMicButton onTranscript={(t) => setQ(t)} size={14}/>
 *
 * The button:
 *   - mounts a fresh hook instance (each search input has its own
 *     recorder so two mics can't race),
 *   - shows a pulsing pink mic while recording,
 *   - fires onTranscript exactly once per recording session (when
 *     the user stops speaking OR taps the mic again),
 *   - silently no-ops if the browser blocks mic permission.
 */
import React, { useEffect } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { useVoiceSearch } from "../lib/useVoiceSearch";


export default function VoiceMicButton({
  onTranscript,
  size = 14,
  className = "",
  testid = "voice-mic-btn",
}) {
  const voice = useVoiceSearch();

  // Push final transcript up to the parent input ONCE per session.
  useEffect(() => {
    if (voice.transcript && !voice.recording) {
      onTranscript?.(voice.transcript);
    }
  }, [voice.transcript, voice.recording, onTranscript]);

  const Icon = voice.busy ? Loader2 : voice.recording ? Square : Mic;

  return (
    <button type="button"
      onClick={voice.recording ? voice.stop : voice.start}
      disabled={voice.busy}
      data-testid={testid}
      title={voice.supported
        ? (voice.recording ? "Stop listening" : "Voice search")
        : (voice.busy ? "Transcribing…" : "Voice search (Whisper)")}
      className={`shrink-0 inline-flex items-center justify-center rounded-full transition active:scale-95 ${
        voice.recording
          ? "bg-[#FF007F] text-white animate-pulse"
          : "bg-[#3D2845] text-[#D4FF00] hover:bg-[#D4FF00] hover:text-black"
      } ${className}`}
      aria-label="Voice search">
      <Icon size={size} className={voice.busy ? "animate-spin" : voice.recording ? "fill-white" : ""}/>
    </button>
  );
}
